import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { TOUR_STEPS, type TourStep } from '../lib/tourSteps';

interface TourContextValue {
  tourActive: boolean;
  currentStep: number;
  currentStepData: TourStep | null;
  tourCompleted: boolean;
  totalSteps: number;
  nextStep: () => void;
  skipTour: () => void;
  finishTour: () => void;
}

const TourContext = createContext<TourContextValue>({
  tourActive: false,
  currentStep: 0,
  currentStepData: null,
  tourCompleted: true,
  totalSteps: TOUR_STEPS.length,
  nextStep: () => {},
  skipTour: () => {},
  finishTour: () => {},
});

// eslint-disable-next-line react-refresh/only-export-components
export function useTour() {
  return useContext(TourContext);
}

export function TourProvider({ children }: { children: ReactNode }) {
  const [tourActive, setTourActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [tourCompleted, setTourCompleted] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    async function checkTour() {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      setUserId(user.id);

      const { data } = await supabase
        .from('profiles')
        .select('app_tour_completed, app_tour_step_reached')
        .eq('id', user.id)
        .single();

      if (data && !data.app_tour_completed) {
        setTourActive(true);
        setTourCompleted(false);
        setCurrentStep(data.app_tour_step_reached ?? 0);
      } else {
        setTourCompleted(true);
      }
    }
    void checkTour();
  }, []);

  const persistStep = useCallback(async (step: number) => {
    if (!userId) return;
    await supabase
      .from('profiles')
      .update({ app_tour_step_reached: step })
      .eq('id', userId);
  }, [userId]);

  const nextStep = useCallback(() => {
    const next = currentStep + 1;
    if (next >= TOUR_STEPS.length) {
      void finishTour();
      return;
    }
    setCurrentStep(next);
    void persistStep(next);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, persistStep]);

  const finishTour = useCallback(async () => {
    setTourActive(false);
    setTourCompleted(true);
    if (userId) {
      await supabase
        .from('profiles')
        .update({ app_tour_completed: true, app_tour_step_reached: TOUR_STEPS.length })
        .eq('id', userId);
    }
  }, [userId]);

  const skipTour = useCallback(() => {
    void finishTour();
  }, [finishTour]);

  const currentStepData = tourActive && currentStep < TOUR_STEPS.length ? TOUR_STEPS[currentStep] : null;

  return (
    <TourContext.Provider value={{
      tourActive,
      currentStep,
      currentStepData,
      tourCompleted,
      totalSteps: TOUR_STEPS.length,
      nextStep,
      skipTour,
      finishTour,
    }}>
      {children}
    </TourContext.Provider>
  );
}
